import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type JSX, type ReactNode, useState } from 'react';

import { AppErrorBoundary } from './AppErrorBoundary';
import { LocaleProvider } from './LocaleProvider';
import { ThemeProvider } from './ThemeProvider';

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * TanStack Query is the only server-state cache; never mirror server data into
 * a Zustand store (CLAUDE.md §7.3).
 */
export function AppProviders({ children }: AppProvidersProps): JSX.Element {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // slot availability is volatile: short staleTime, refetch on focus,
            // and the server re-verifies anyway (CLAUDE.md §7.4)
            staleTime: 30_000,
            refetchOnWindowFocus: true,
            retry: 1,
          },
          mutations: { retry: 0 },
        },
      }),
  );

  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <LocaleProvider>
          <ThemeProvider>{children}</ThemeProvider>
        </LocaleProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
